import React, { useState, useMemo } from 'react';

const ContactSelector = ({ 
  contacts = [], 
  selectedContactId, 
  onSelectContact,
  placeholder = "Select a contact...",
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter contacts based on search term
  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;
    const term = searchTerm.toLowerCase();
    return contacts.filter(contact => 
      contact.name.toLowerCase().includes(term) || 
      contact.email.toLowerCase().includes(term) ||
      contact.role.toLowerCase().includes(term)
    );
  }, [contacts, searchTerm]);

  // Find the selected contact
  const selectedContact = contacts.find(contact => contact.id === selectedContactId);

  const handleSelect = (contact) => {
    onSelectContact(contact);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.contact-selector')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`contact-selector relative ${className}`}>
      {/* Selected contact display */}
      <div 
        className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition cursor-pointer flex items-center justify-between"
        onClick={handleToggle}
      >
        <div className="flex items-center">
          {selectedContact ? (
            <>
              <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs mr-2">
                {selectedContact.name.charAt(0).toUpperCase()}
              </div>
              <span>{selectedContact.name}</span>
              {selectedContact.role && (
                <span className="ml-2 text-xs text-dark-text-secondary">({selectedContact.role})</span>
              )}
            </>
          ) : (
            <span className="text-dark-text-secondary">{placeholder}</span>
          )}
        </div>
        <svg 
          className={`w-4 h-4 text-dark-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-dark-surface border border-dark-border rounded-md shadow-lg max-h-60 overflow-auto">
          {/* Search input */}
          <div className="p-2">
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full bg-dark-bg border border-dark-border rounded-md p-2 text-sm text-dark-text-primary focus:ring-1 focus:ring-brand-primary focus:outline-none transition"
              autoFocus
            />
          </div>
          
          {/* Contacts list */}
          <div className="py-1">
            {filteredContacts.length > 0 ? (
              filteredContacts.map(contact => (
                <div
                  key={contact.id}
                  className={`px-4 py-2 cursor-pointer flex items-center ${
                    selectedContactId === contact.id 
                      ? 'bg-brand-primary/20 text-brand-primary' 
                      : 'hover:bg-dark-bg'
                  }`}
                  onClick={() => handleSelect(contact)}
                >
                  <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs mr-3">
                    {contact.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium">{contact.name}</div>
                    {contact.email && (
                      <div className="text-xs text-dark-text-secondary">{contact.email}</div>
                    )}
                    {contact.role && (
                      <div className="text-xs text-dark-text-secondary">{contact.role}</div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-dark-text-secondary text-center">
                No contacts found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSelector;